import { FunctionComponent } from 'react';
import { chakra } from '@chakra-ui/react';

import { useAppSelector } from '@/store';
import { AdAttribute, AdState } from '@/store/ad/types';
import { RangeIntegerInput } from './RangeIntegerInput';
import { CheckboxInput } from './CheckboxInput';
import { DateInput } from './DateInput';
import { HtmlInput } from './HtmlInput';
import { NumberInput } from './NumberInput';
import { SelectionInput } from './SelectionInput';
import { TextInput } from './TextInput';
import { TextTypeahead } from './TextTypeahead';

export type AttributeInputProps = {
  attribute: AdAttribute;
  parentAttribute?: AdAttribute;
};

const AttributeContainer = chakra('div', {
  baseStyle: {
    position: 'relative',
  },
});

const CharacterCount = chakra('span', {
  baseStyle: {
    position: 'absolute',
    top: -6,
    right: 0,
    fontSize: 'sm',
    color: 'neutral.300',
  },
});

export const AttributeInput: FunctionComponent<AttributeInputProps> = ({ attribute, parentAttribute }) => {
  const { formData } = useAppSelector((state: { ad: AdState }) => state.ad);
  const value = formData[attribute.id]?.value || '';
  const maxLength = attribute.maxLength;

  if (!attribute.name || !attribute.editable) {
    return null;
  }

  const showCharCount =
    typeof maxLength === 'number' && maxLength > 0 && (typeof value === 'string' || typeof value === 'number');

  const charCount = showCharCount ? (
    <CharacterCount>
      {value.length} / {maxLength}
    </CharacterCount>
  ) : null;

  switch (attribute.type) {
    case 'SelectSingle':
    case 'SelectSingleSearchMulti':
    case 'SelectMultiAnd':
    case 'SelectMultiOr':
    case 'SelectMultiSearchSingle':
      return attribute.items ? <SelectionInput attribute={attribute} parentAttribute={parentAttribute} /> : null;

    case 'HTML':
    case 'HTMLEInserat':
    case 'Markdown':
      return attribute.name ? (
        <HtmlInput
          id={attribute.id}
          useMarkdown={attribute.type === 'Markdown'}
          allowImages={
            typeof attribute.settings['EditorAllowImages'] === 'string' &&
            attribute.settings['EditorAllowImages'].toLowerCase() === 'true'
          }
          allowLinks={
            typeof attribute.settings['EditorAllowLinks'] === 'string' &&
            attribute.settings['EditorAllowLinks'].toLowerCase() === 'true'
          }
          allowLists={
            typeof attribute.settings['EditorAllowLists'] === 'string' &&
            attribute.settings['EditorAllowLists'].toLowerCase() === 'true'
          }
        />
      ) : null;

    case 'RangeInteger':
      return <RangeIntegerInput attribute={attribute} />;

    case 'Date':
      return <DateInput attribute={attribute} />;

    case 'Boolean':
      return <CheckboxInput id={attribute.id} name={attribute.name} />;

    case 'Integer':
    case 'Decimal':
    case 'Price':
      return (
        <AttributeContainer>
          <NumberInput attribute={attribute} />
          {charCount}
        </AttributeContainer>
      );

    case 'NormalizedText':
      return (
        <AttributeContainer>
          <TextTypeahead attribute={attribute} parentAttribute={parentAttribute} />
          {charCount}
        </AttributeContainer>
      );

    default:
      return (
        <AttributeContainer>
          <TextInput attribute={attribute} />
          {charCount}
        </AttributeContainer>
      );
  }
};
